/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */

/**
 * The kind of value a FilterOption holds. Drives which input control the
 * filter UI renders and how the value is serialized for the backend.
 */
export enum FilterType {
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
}

/**
 * A single, self-contained filter option shown to the user.
 *
 * Unlike the older approach of scattering the column, label and entered value
 * across separate component variables, a FilterOption carries everything it
 * needs: the backend column to filter on, the label to display, its value
 * type, and the currently selected/entered value.
 *
 * Keeping filters as discrete objects positions the pages to support multiple
 * active filters in the future (a list of FilterOption) rather than a single
 * selected filter.
 */
export class FilterOption {
  /** Backend column to filter on. A null column represents "no filter". */
  column: string = null;

  /** Human-readable label shown in the filter dropdown / label. */
  label: string = '';

  /** The value type, which determines the input control and serialization. */
  type: FilterType = FilterType.STRING;

  /** The value the user has selected/entered for this filter. */
  value: any = '';

  constructor(init?: Partial<FilterOption>) {
    Object.assign(this, init);
  }

  /**
   * The value formatted the way the paging/filter APIs expect it. Booleans
   * are serialized to '1'/'0' (replacing the old hand-coded special-case),
   * everything else is passed through as a string.
   */
  get apiValue(): string {
    if (this.type === FilterType.BOOLEAN) {
      return this.value ? '1' : '0';
    }
    return this.value == null ? '' : String(this.value);
  }
}

/** Shared "REMOVE FILTER" option used to clear the active filter. */
export const REMOVE_FILTER = new FilterOption({ column: null, label: 'REMOVE FILTER' });

/**
 * Serializes a list of active filters into the repeated query params the paging
 * APIs expect - one (filterColumns, filterStrings, filterTypes) triple per
 * usable filter, which the backend AND's together. Returns a string beginning
 * with '&' (or '' when there are no usable filters) to append to a query URL.
 */
export function buildFilterParams(filters: FilterOption[]): string {
  let params = '';
  (filters || []).forEach(filter => {
    const value = filter == null ? null : filter.apiValue;
    if (filter != null && filter.column != null && value != null && `${value}`.length > 0) {
      params += `&filterColumns=${encodeURIComponent(filter.column)}`;
      params += `&filterStrings=${encodeURIComponent(value)}`;
      params += `&filterTypes=${encodeURIComponent(filter.type)}`;
    }
  });
  return params;
}
